/* ============================================================================
   Making the seeded email actually editable
   ----------------------------------------------------------------------------
   Joshua Diokno, 11 Sep 2026 (CRM-2026-00047): "Placing the link is challenging
   as it lands arbitrarily on any line of text in the composer; moving it around
   also feels limited" and "I was under the impression that when the designer is
   opened, a user can freely compose and manually add texts... Apparently this is
   not the case."

   Both come from the SHAPE of the HTML we hand GrapesJS, not from the designer.

   - Find Buyers seeds the canvas with the GeeVee message run through
     `text.replace(/\n/g, '<br>')`. That is a flat run of bare text nodes and
     <br>s directly under the wrapper. A lone text node is not a text COMPONENT,
     and the wrapper is not one either, so double-clicking it opens nothing -
     there is genuinely no way to type. Dropping anything into that soup lands
     between two arbitrary text nodes, which is the link "landing on any line",
     and a text node is not a container you can drag it back out of.
   - Template/campaign seeds are table-based, and GrapesJS types every <td> as
     `cell` (ComponentTableCell.isComponent). A typed component skips the
     parser's "all children are text, so the parent is text" promotion, so text
     sitting bare in a <td> is not editable either.

   So before seeding we give the text a body to live in: loose top-level content
   becomes one editable paragraph per blank line, and bare text in a <td> gets
   wrapped in an editable div. Paragraphs are also proper drop targets, so a
   dragged link lands on its own line where it was aimed instead of being nudged
   into the middle of a sentence.
   ========================================================================== */

// Tags whose bare text GrapesJS cannot make editable on its own, because the tag
// already resolves to a typed component (cell/row/table) or is the document root.
const NEEDS_TEXT_WRAPPER = ["TD", "TH", "BODY"];
// Marks a div as a GrapesJS text component, so it is double-click editable.
export const TEXT_DIV_OPEN =
  '<div data-gjs-type="text" style="font-family:Arial,sans-serif;font-size:15px;line-height:1.6;color:#1f2933;">';

const INLINE_TAGS = [
  "A", "B", "STRONG", "I", "EM", "U", "S", "SPAN", "SMALL", "SUB", "SUP",
  "CODE", "MARK", "ABBR", "IMG", "FONT", "BR",
];

/** Does this node carry anything a person would call text? */
function hasOwnText(el) {
  return Array.from(el.childNodes).some(
    (n) => n.nodeType === 3 && (n.nodeValue || "").trim() !== ""
  );
}

/**
 * Group an element's loose inline children into editable paragraph divs, split
 * on blank lines (a <br><br> run), so each paragraph of a GeeVee message becomes
 * its own text block: separately editable, separately draggable, and a clean
 * place for a link to land.
 */
function wrapLooseText(doc, el) {
  const kids = Array.from(el.childNodes);
  const para = () => doc.createElement("div");
  const out = [];
  let current = null;
  let blanks = 0;

  const flush = () => {
    if (current && current.innerHTML.trim()) out.push(current);
    current = null;
  };

  kids.forEach((node) => {
    const tag = node.nodeType === 1 ? node.tagName.toUpperCase() : "";
    if (tag === "BR") {
      // One <br> is a line break inside the paragraph; two in a row end it.
      blanks += 1;
      if (blanks >= 2) flush();
      else if (current) current.appendChild(node.cloneNode(true));
      return;
    }
    const isBlankText = node.nodeType === 3 && (node.nodeValue || "").trim() === "";
    if (isBlankText) return; // whitespace between nodes decides nothing
    blanks = 0;
    // A block-level element is already its own thing - leave it alone.
    if (tag && !INLINE_TAGS.includes(tag)) {
      flush();
      out.push(node.cloneNode(true));
      return;
    }
    if (!current) current = para();
    current.appendChild(node.cloneNode(true));
  });
  flush();

  el.innerHTML = "";
  const wrapped = [];
  out.forEach((node) => {
    if (node.tagName === "DIV" && !node.getAttribute("data-gjs-type")) {
      const holder = doc.createElement("div");
      holder.innerHTML = `${TEXT_DIV_OPEN}${node.innerHTML}</div>`;
      const made = holder.firstChild;
      wrapped.push(made);
      el.appendChild(made);
    } else {
      el.appendChild(node);
    }
  });

  // The blank lines we split on WERE the spacing between paragraphs. Turning a
  // <br><br> into two divs without it would silently close the gaps up and the
  // email would go out looking different from the one that was written. Only
  // when there is more than one paragraph, so wrapping a single cell's text
  // changes nothing about how it sits.
  if (wrapped.length > 1) {
    wrapped.forEach((node, i) => {
      if (i === wrapped.length - 1) return;
      node.setAttribute("style", `${node.getAttribute("style") || ""}margin:0 0 16px;`);
    });
  }
}

/**
 * Reshape seeded HTML so every word in it is editable and every block is a real
 * drop target. Returns the original string untouched if anything goes wrong -
 * a seed that will not parse must still reach the canvas.
 */
export function normaliseSeedHtml(raw) {
  const input = typeof raw === "string" ? raw : "";
  if (!input.trim()) return input;
  if (typeof DOMParser === "undefined") return input;
  try {
    const doc = new DOMParser().parseFromString(input, "text/html");
    const body = doc.body;
    if (!body) return input;

    // Bare text inside a <td>/<th> (or straight under the body) is not editable
    // until it sits in something GrapesJS types as text.
    NEEDS_TEXT_WRAPPER.forEach((tag) => {
      const sel = tag === "BODY" ? [body] : Array.from(body.querySelectorAll(tag.toLowerCase()));
      sel.forEach((el) => {
        if (!hasOwnText(el) && !Array.from(el.childNodes).some((n) => n.nodeName === "BR")) return;
        wrapLooseText(doc, el);
      });
    });

    // Keep any <style> the seed carried; the canvas reads it for the design.
    const styles = Array.from(doc.head ? doc.head.querySelectorAll("style") : [])
      .map((s) => s.outerHTML)
      .join("");
    const out = `${styles}${body.innerHTML}`;
    return out.trim() ? out : input;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("EmailBuilder: could not normalise the seeded HTML, using it as-is", e);
    return input;
  }
}
